(function () {
  const controllerMap = new WeakMap();
  let jumpInProgress = false;

  const HEADING_SELECTOR = '.tt-blog-article__body h2';

  const JUMP_GAP = 8;
  const SMOOTH_DURATION = 560;

  function slugify(text) {
    return text
      .toLowerCase()
      .trim()
      .replace(/[^\w\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-');
  }

  function getArticleElement() {
    return document.querySelector('[data-blog-article]');
  }

  function prepareHeaderForJump() {
    const header = document.querySelector('.section-header');
    if (!header) return;

    header.classList.add('shopify-section-header-sticky', 'animate', 'scrolled-past-header');
    header.classList.remove('shopify-section-header-hidden');

    const sticky = document.querySelector('sticky-header');
    if (sticky && typeof sticky.setHeaderHeight === 'function') {
      sticky.setHeaderHeight();
    }
  }

  function getStickyHeaderHeight() {
    const parsed = parseInt(
      getComputedStyle(document.documentElement).getPropertyValue('--header-height'),
      10
    );
    return Number.isFinite(parsed) && parsed > 0 ? parsed : 80;
  }

  function getAnnouncementBottom() {
    const bar = document.querySelector('.announcement-bar-section');
    if (!bar) return 0;
    return bar.offsetTop + bar.offsetHeight;
  }

  function getJumpScrollOffset(targetScrollY) {
    const scrollY = Number.isFinite(targetScrollY) ? targetScrollY : window.scrollY;
    const headerHeight = getStickyHeaderHeight();
    const announcementBottom = getAnnouncementBottom();

    if (announcementBottom <= 0 || scrollY >= announcementBottom) {
      return headerHeight + JUMP_GAP;
    }

    return Math.max(announcementBottom - scrollY, 0) + headerHeight + JUMP_GAP;
  }

  function syncJumpScrollOffset(targetScrollY) {
    document.documentElement.style.setProperty(
      '--tt-blog-jump-offset',
      getJumpScrollOffset(targetScrollY) + 'px'
    );
  }

  function computeTargetScrollTop(jumpTarget) {
    prepareHeaderForJump();

    const docTop = jumpTarget.getBoundingClientRect().top + window.scrollY;
    let scrollTop = docTop - getJumpScrollOffset(docTop);
    scrollTop = docTop - getJumpScrollOffset(scrollTop);

    return Math.max(scrollTop, 0);
  }

  function getArticleRoot() {
    return document.querySelector('main[data-template="article"]') || document;
  }

  function getJumpTarget(element) {
    if (!element) return null;
    if (element.matches(HEADING_SELECTOR)) return element;
    return element.querySelector(HEADING_SELECTOR) || element;
  }

  function assignHeadingAnchor(heading, id) {
    heading.id = id;
  }

  function collectHeadings() {
    return Array.from(getArticleRoot().querySelectorAll(HEADING_SELECTOR)).filter((heading) =>
      heading.textContent.trim()
    );
  }

  function getHeadingId(heading, index, usedIds) {
    let id =
      heading.dataset.tocId ||
      heading.id ||
      slugify(heading.textContent || '') ||
      'section-' + (index + 1);

    while (usedIds.has(id)) {
      id = id + '-' + (index + 1);
    }

    assignHeadingAnchor(heading, id);
    usedIds.add(id);
    return id;
  }

  function easeInOutCubic(t) {
    return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
  }

  function smoothScrollTo(targetY, onComplete) {
    const startY = window.scrollY;
    const distance = targetY - startY;

    if (Math.abs(distance) <= 2) {
      onComplete();
      return;
    }

    const startTime = performance.now();

    function step(now) {
      const progress = Math.min((now - startTime) / SMOOTH_DURATION, 1);
      window.scrollTo(0, startY + distance * easeInOutCubic(progress));

      if (progress < 1) {
        requestAnimationFrame(step);
      } else {
        onComplete();
      }
    }

    requestAnimationFrame(step);
  }

  function alignJumpTarget(jumpTarget) {
    prepareHeaderForJump();
    syncJumpScrollOffset(window.scrollY);

    const offset = getJumpScrollOffset(window.scrollY);
    const delta = jumpTarget.getBoundingClientRect().top - offset;

    if (Math.abs(delta) > 1) {
      window.scrollBy({ top: delta, left: 0, behavior: 'auto' });
      return true;
    }

    return false;
  }

  function settleJump(jumpTarget, hash, id, onComplete, attempt) {
    const adjusted = alignJumpTarget(jumpTarget);

    if (adjusted && attempt < 6) {
      requestAnimationFrame(() => settleJump(jumpTarget, hash, id, onComplete, attempt + 1));
      return;
    }

    jumpInProgress = false;
    syncJumpScrollOffset(window.scrollY);
    history.replaceState(null, '', hash);
    onComplete(id);
    document.dispatchEvent(new CustomEvent('tt-blog:after-jump'));
  }

  function jumpToTarget(target, hash, id, onComplete) {
    const jumpTarget = getJumpTarget(target);
    if (!jumpTarget) return;

    jumpInProgress = true;
    document.dispatchEvent(new CustomEvent('tt-blog:before-jump'));
    prepareHeaderForJump();

    requestAnimationFrame(() => {
      const targetY = computeTargetScrollTop(jumpTarget);
      syncJumpScrollOffset(targetY);

      if (Math.abs(window.scrollY - targetY) <= 2) {
        settleJump(jumpTarget, hash, id, onComplete, 0);
        return;
      }

      smoothScrollTo(targetY, () => {
        settleJump(jumpTarget, hash, id, onComplete, 0);
      });
    });
  }

  function destroySection(section) {
    const controller = controllerMap.get(section);
    if (controller) {
      controller.abort();
      controllerMap.delete(section);
    }
    section.dataset.jumpInit = 'false';
  }

  function clearJumpNavLists(navLists) {
    navLists.forEach((navList) => {
      navList.querySelectorAll(':scope > li').forEach((item) => item.remove());
    });
  }

  function buildJumpNavLists(navLists, headings) {
    const usedIds = new Set();

    headings.forEach((heading, index) => {
      const id = getHeadingId(heading, index, usedIds);
      const label = heading.textContent.trim();
      if (!label) return;

      navLists.forEach((navList) => {
        const item = document.createElement('li');
        const link = document.createElement('a');
        link.className = 'tt-blog-article__jump-link';
        link.href = '#' + id;
        link.textContent = label;
        item.appendChild(link);
        navList.appendChild(item);
      });
    });
  }

  function initSection(section) {
    destroySection(section);

    const navLists = section.querySelectorAll('[data-jump-nav]');
    if (navLists.length === 0) return;

    const headings = collectHeadings();
    const emptyMessages = section.querySelectorAll('[data-jump-empty]');

    clearJumpNavLists(navLists);

    if (headings.length === 0) {
      emptyMessages.forEach((empty) => {
        empty.hidden = false;
      });
      return;
    }

    emptyMessages.forEach((empty) => {
      empty.hidden = true;
    });

    buildJumpNavLists(navLists, headings);

    const links = section.querySelectorAll('.tt-blog-article__jump-link');
    if (links.length === 0) return;

    const targets = [];
    links.forEach((link) => {
      const id = (link.getAttribute('href') || '').replace('#', '');
      const el = id ? document.getElementById(id) : null;
      if (el) targets.push({ id, el, jumpEl: getJumpTarget(el) });
    });

    const setActive = (id) => {
      if (!id) return;
      links.forEach((link) => {
        const targetId = (link.getAttribute('href') || '').replace('#', '');
        link.classList.toggle('is-active', targetId === id);
      });
    };

    const updateActiveFromScroll = () => {
      if (jumpInProgress || targets.length === 0) return;

      const offset = getJumpScrollOffset(window.scrollY);
      let activeId = targets[0].id;

      targets.forEach(({ id, jumpEl }) => {
        if (jumpEl && jumpEl.getBoundingClientRect().top <= offset + 2) {
          activeId = id;
        }
      });

      setActive(activeId);
    };

    const controller = new AbortController();
    controllerMap.set(section, controller);
    const signal = controller.signal;

    links.forEach((link) => {
      link.addEventListener(
        'click',
        (event) => {
          const href = link.getAttribute('href');
          if (!href || href.charAt(0) !== '#') return;

          const target = document.getElementById(href.slice(1));
          if (!target) return;

          event.preventDefault();
          jumpToTarget(target, href, href.slice(1), setActive);
        },
        { signal }
      );
    });

    updateActiveFromScroll();

    let ticking = false;
    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(() => {
        syncJumpScrollOffset(window.scrollY);
        updateActiveFromScroll();
        ticking = false;
      });
    };

    window.addEventListener('scroll', onScroll, { passive: true, signal });
    window.addEventListener(
      'resize',
      () => {
        prepareHeaderForJump();
        syncJumpScrollOffset(window.scrollY);
        onScroll();
      },
      { passive: true, signal }
    );

    section.dataset.jumpInit = 'true';
  }

  function initAll() {
    prepareHeaderForJump();
    syncJumpScrollOffset(window.scrollY);
    normalizeArticleBodyMedia();
    document.querySelectorAll('[data-blog-article]').forEach((section) => {
      initSection(section);
      scheduleJumpRetry(section);
    });
  }

  function scheduleJumpRetry(section, attempt) {
    const retryAttempt = attempt || 0;
    if (retryAttempt >= 12) return;

    const headings = collectHeadings();
    const navLists = section.querySelectorAll('[data-jump-nav]');
    const listCount = navLists.length ? navLists[0].querySelectorAll(':scope > li').length : 0;
    const emptyMessage = section.querySelector('[data-jump-empty]');
    const emptyVisible = emptyMessage && !emptyMessage.hidden;

    if (headings.length > 0 && listCount === headings.length) return;
    if (headings.length === 0 && (emptyVisible || listCount === 0)) return;

    window.setTimeout(() => {
      initSection(section);
      scheduleJumpRetry(section, retryAttempt + 1);
    }, 120 * (retryAttempt + 1));
  }

  function normalizeArticleBodyMedia() {
    document.querySelectorAll('.tt-blog-article__body.rte').forEach((root) => {
      wrapLooseTextBlocks(root);

      root.querySelectorAll('img').forEach((img) => {
        let prev = img.previousSibling;
        while (prev) {
          if (prev.nodeName === 'BR') {
            const remove = prev;
            prev = prev.previousSibling;
            remove.remove();
            continue;
          }
          if (prev.nodeType === Node.TEXT_NODE && !prev.textContent.trim()) {
            const remove = prev;
            prev = prev.previousSibling;
            remove.remove();
            continue;
          }
          break;
        }

        let next = img.nextSibling;
        while (next) {
          if (next.nodeName === 'BR') {
            const remove = next;
            next = next.nextSibling;
            remove.remove();
            continue;
          }
          if (next.nodeType === Node.TEXT_NODE && !next.textContent.trim()) {
            const remove = next;
            next = next.nextSibling;
            remove.remove();
            continue;
          }
          break;
        }

        const parent = img.parentElement;
        if (!parent || parent === root) {
          const wrap = document.createElement('div');
          wrap.className = 'tt-blog-article__media-block';
          img.replaceWith(wrap);
          wrap.appendChild(img);
        } else if (parent.tagName === 'P' && parent.children.length === 1 && !parent.textContent.trim()) {
          parent.classList.add('tt-blog-article__media-block');
        }
      });
    });
  }

  function isBodyBlockElement(node) {
    if (node.nodeType !== Node.ELEMENT_NODE) return false;
    if (node.classList.contains('tt-blog-article__body-excerpt')) return true;
    if (node.classList.contains('tt-blog-article__media-block')) return true;
    return node.matches('h1,h2,h3,h4,h5,h6,p,ul,ol,blockquote,figure,table');
  }

  function wrapLooseTextBlocks(root) {
    const nodes = Array.from(root.childNodes);
    let buffer = [];

    function flush() {
      if (buffer.length === 0) return;

      const first = buffer[0];
      const paragraph = document.createElement('p');
      root.insertBefore(paragraph, first);

      buffer.forEach((node) => {
        paragraph.appendChild(node);
      });
      buffer = [];

      if (!paragraph.textContent.replace(/\s/g, '')) {
        paragraph.remove();
      }
    }

    nodes.forEach((node) => {
      if (isBodyBlockElement(node)) {
        flush();
        return;
      }

      if (node.nodeType === Node.TEXT_NODE || node.nodeName === 'BR') {
        if (node.nodeType === Node.TEXT_NODE && !node.textContent.trim()) {
          node.remove();
          return;
        }
        buffer.push(node);
      }
    });

    flush();
  }

  function scheduleInit() {
    window.requestAnimationFrame(initAll);
  }

  document.addEventListener('DOMContentLoaded', initAll);
  document.addEventListener('shopify:section:load', scheduleInit);
  document.addEventListener('shopify:section:unload', (event) => {
    event.target.querySelectorAll('[data-blog-article]').forEach(destroySection);
    scheduleInit();
  });
  document.addEventListener('shopify:section:reorder', scheduleInit);
  document.addEventListener('shopify:block:select', scheduleInit);
  document.addEventListener('shopify:block:deselect', scheduleInit);
})();
